import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { setImpersonationGuardHandler } from "@/lib/queryClient";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

type ImpersonationStatus = {
  active: boolean;
  targetUser?: { id: number; email: string; name: string } | null;
};

type PendingAction = {
  method: string;
  url: string;
  resolve: (confirmed: boolean) => void;
};

type ImpersonationGuardContextValue = {
  isImpersonating: boolean;
  targetUser: { id: number; email: string; name: string } | null;
};

const ImpersonationGuardContext = createContext<ImpersonationGuardContextValue>({
  isImpersonating: false,
  targetUser: null,
});

export function useImpersonationGuard() {
  return useContext(ImpersonationGuardContext);
}

function describeAction(method: string, url: string): string {
  const upper = method.toUpperCase();
  const path = url.replace(/^\/api\/app\//, "").replace(/\/\d+/g, "");

  if (upper === "DELETE") {
    return `Delete ${path.split("/").pop() || "resource"}`;
  }

  if (/\/send\b/.test(url)) return "Send a message or notification";
  if (/\/broadcast\b/.test(url)) return "Send a broadcast message";
  if (/\/apply\b/.test(url)) return "Apply changes";
  if (/\/sync\b/.test(url)) return "Sync data with external service";
  if (/\/archive\b/.test(url)) return "Archive a record";
  if (/\/remove\b/.test(url)) return "Remove a record";
  if (/\/destroy\b/.test(url)) return "Permanently destroy a record";
  if (/\/delete\b/.test(url)) return "Delete a record";

  return `${upper} ${path}`;
}

export function ImpersonationGuardProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingAction | null>(null);

  const statusQuery = useQuery<ImpersonationStatus>({
    queryKey: ["/api/app/impersonation/status"],
    queryFn: async () => {
      const res = await fetch("/api/app/impersonation/status", { credentials: "include" });
      if (!res.ok) return { active: false };
      return res.json();
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const status = statusQuery.data;
  const isImpersonating = !!status?.active && !!status?.targetUser;
  const targetUser = status?.targetUser ?? null;

  const statusRef = useRef({ isImpersonating, targetUser });
  statusRef.current = { isImpersonating, targetUser };

  const pendingRef = useRef(false);

  const guardHandler = useCallback(
    (method: string, url: string): Promise<boolean> => {
      if (!statusRef.current.isImpersonating) {
        return Promise.resolve(true);
      }
      if (pendingRef.current) {
        return Promise.resolve(false);
      }
      pendingRef.current = true;
      return new Promise<boolean>((resolve) => {
        setPending({
          method,
          url,
          resolve: (confirmed: boolean) => {
            pendingRef.current = false;
            resolve(confirmed);
          },
        });
      });
    },
    [],
  );

  useEffect(() => {
    if (isImpersonating) {
      setImpersonationGuardHandler(guardHandler);
    } else {
      setImpersonationGuardHandler(null);
    }
    return () => setImpersonationGuardHandler(null);
  }, [isImpersonating, guardHandler]);

  const handleConfirm = useCallback(() => {
    pending?.resolve(true);
    setPending(null);
  }, [pending]);

  const handleCancel = useCallback(() => {
    pending?.resolve(false);
    setPending(null);
  }, [pending]);

  const contextValue: ImpersonationGuardContextValue = {
    isImpersonating,
    targetUser,
  };

  return (
    <ImpersonationGuardContext.Provider value={contextValue}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent data-testid="dialog-impersonation-guard">
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-amber-600">
              <ShieldAlert className="h-5 w-5" />
              <AlertDialogTitle data-testid="text-impersonation-guard-title">
                Impersonation Write Confirmation
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-800 text-sm">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    You are acting as{" "}
                    <strong data-testid="text-impersonation-guard-user">
                      {targetUser?.name || targetUser?.email}
                    </strong>
                    {targetUser?.name && (
                      <span className="text-amber-600"> ({targetUser.email})</span>
                    )}
                    . This action will affect real data.
                  </span>
                </div>
                {pending && (
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">Action:</span>{" "}
                    <span data-testid="text-impersonation-guard-action">
                      {describeAction(pending.method, pending.url)}
                    </span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancel}
              data-testid="button-impersonation-guard-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="button-impersonation-guard-confirm"
            >
              Confirm Action
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ImpersonationGuardContext.Provider>
  );
}
